import React, { ReactNode } from 'react';
import type { ValidationRule } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { checkSICCodeExistence } from '@insurancemenu/imenu-components-lib/dist/utils/forms/sic';

function createLink(email: string): ReactNode {
  return (
    <a href={`mailto:${email}`} rel="noreferrer">
      {email}
    </a>
  );
}

/** Sun Life–specific hard blocks passed as `sic` capability `additionalValidations` (existence is built into the lib). */
export const sunLifeSICAdditionalValidations: ValidationRule[] = [
  {
    check: (value: string): boolean => !(value === '8021' || value === '8072' || value === '7941'),
    msg: (
      <>
        Coverage is not available for dental insurance. For other lines of coverage, request for proposal (RFP) can be
        emailed to {createLink('digital@sunlife.com')}.
      </>
    ),
  },
  {
    check: (value: string): boolean => !(value === '8042'),
    msg: (
      <>
        Coverage is not available for vision insurance. For other lines of coverage, request for proposal (RFP) can be
        emailed to {createLink('digital@sunlife.com')}.
      </>
    ),
  },
  {
    check: (value: string): boolean => !(value === '8811'),
    msg: 'Coverage is not available for this SIC code.',
  },
  {
    check: (value: string): boolean =>
      !(
        value === '8011' ||
        value === '8031' ||
        value === '8041' ||
        value === '8043' ||
        value === '8049' ||
        value === '8071' ||
        value === '8092' ||
        value === '8093'
      ),
    msg: (
      <>
        We are not offering coverage for this SIC code through Insurance Menu. Request for proposal (RFP) can be emailed
        to {createLink('digital@sunlife.com')}.
      </>
    ),
  },
];

/**
 * Non-blocking warning copy for the SIC `Alert`, or `null` when there is no warning.
 *
 * Hard-block codes and warning prefixes do not overlap, so invalid codes only show the field message.
 * If a SIC ever matches both an error rule and a warning rule, add an early guard here (e.g. return `null` for those
 * codes) so users do not see an error and the warning note at the same time.
 */
export function sunLifeSICWarningMessage(value: string): string | null {
  if (value.length !== 4 || !checkSICCodeExistence(value)) return null;
  if (value.startsWith('40')) {
    return 'You can quote dental and vision through Insurance Menu. Coverage is not available for life, disability and supplemental health.';
  }
  if (
    value.startsWith('44') ||
    value.startsWith('45') ||
    value.startsWith('10') ||
    value.startsWith('12') ||
    value.startsWith('14') ||
    value === '1381'
  ) {
    return 'You can quote dental, vision and supplemental health through Insurance Menu. Coverage is not available for life and disability';
  }
  return null;
}
